// src/components/GuessInput.js

import React, { useState } from 'react';
import Autocomplete from 'react-autocomplete';
import charactersData from '../data/characters.json';

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function GuessInput({ onGuess, guessedCharacters, onImageHover, onImageMouseLeave }) {
  const [inputValue, setInputValue] = useState('');
  const characters = Object.values(charactersData);

  // Filter characters to only include those with guessable = true
  const guessableCharacters = characters.filter(
    (char) => char.guessable === true
  );

  // Create a set of guessed character names
  const guessedNamesSet = new Set(guessedCharacters.map((g) => g.name));

  // Filter out guessed characters
  const availableCharacters = guessableCharacters.filter(
    (char) => !guessedNamesSet.has(char.name)
  );

  // Sort available characters by name (case-insensitive)
  availableCharacters.sort((a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase())
  );

  // Maximum number of suggestions to display
  const maxSuggestions = 190;

  // Filter and limit the suggestions based on user input
  const filteredCharacters = availableCharacters
    .filter((item) =>
      item.name.toLowerCase().includes(inputValue.toLowerCase())
    )
    .slice(0, maxSuggestions);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedInput = inputValue.trim();
    if (trimmedInput === '') {
      alert('Please enter a character name.');
      return;
    }
    // Check if character is guessable
    const character = guessableCharacters.find(
      (char) => char.name.toLowerCase() === trimmedInput.toLowerCase()
    );
    if (!character) {
      alert('Character not found or not guessable!');
      return;
    }
    // Check if character has already been guessed
    if (guessedNamesSet.has(trimmedInput)) {
      alert('You have already guessed this character.');
      return;
    }
    onGuess(trimmedInput);
    setInputValue('');
  };

  return (
    <form onSubmit={handleSubmit} className="mb-8 flex justify-center">
      <div className="flex items-center ">
        <Autocomplete
          getItemValue={(item) => item.name}
          items={filteredCharacters}
          renderItem={(item, isHighlighted) => {
            // Escape special regex characters
            const escapedInputValue = escapeRegExp(inputValue);

            // Split the name appropriately
            let parts;
            if (escapedInputValue) {
              const regex = new RegExp(`(${escapedInputValue})`, 'i');
              parts = item.name.split(regex);
            } else {
              parts = [item.name];
            }

            // Encode the character name for URL
            const encodedName = encodeURIComponent(item.name);

            // Image path
            const imagePath = `/characters/${encodedName}/image_1.png`;
            const imageFallback = `/characters/${encodedName}/image_1.jpg`;

            return (
              <div
                key={item.name}
                className={`group flex items-center text-white px-2 py-1 relative ${
                  isHighlighted
                    ? 'bg-palette-lighter-blue'
                    : 'bg-palette-dark-blue'
                }`}
              >
                <img
                  src={imagePath}
                  alt={item.name}
                  className="w-8 h-8 rounded-full mr-2 cursor-pointer"
                  data-fallback={imageFallback}
                  onError={(e) => {
                    if (e.target.src.endsWith('image_1.png')) {
                      e.target.src = imageFallback;
                    } else {
                      e.target.onerror = null;
                      e.target.src = '/default-character.png';
                    }
                  }}
                  onMouseEnter={() => onImageHover(imagePath)}
                  onMouseLeave={onImageMouseLeave}
                />
                {parts.map((part, index) =>
                  escapedInputValue &&
                  part.toLowerCase() === inputValue.toLowerCase() ? (
                    <span key={index} className="font-bold text-indigo-600">
                      {part}
                    </span>
                  ) : (
                    <span key={index}>{part}</span>
                  )
                )}
              </div>
            );
          }}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onSelect={(val) => setInputValue(val)}
          inputProps={{
            placeholder: 'Enter character name',
            className:
              'w-96 px-4 bg-palette-dark-blue py-2 text-white border border-palette-lighter-blue rounded-l-md focus:outline-none focus:ring-2 focus:ring-indigo-500',
          }}
          wrapperStyle={{ display: 'inline-block', width: 'auto' }}
          menuStyle={{
            maxHeight: '400px',
            overflowY: 'auto',
            position: 'absolute',
            border: '1px solid #48A9A6',
            zIndex: 21,
          }}
        />
        <button
          type="submit"
          className="px-4 py-2 bg-palette-lighter-blue text-white rounded-r-md hover:bg-palette-light-rose hover:text-black focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          Guess
        </button>
      </div>
    </form>
  );
}

export default GuessInput;
